// ============================================
// Settings Page - User and System Settings
// ============================================

import { useState } from 'react';
import { 
  Moon, 
  Sun, 
  Globe, 
  Bell, 
  Shield, 
  LogOut,
  Save,
  User,
  Building
} from 'lucide-react';
import { useAuthStore, useUIStore, useToast } from '../stores';
import { cn } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

export function Settings() {
  const toast = useToast();
  const { t, language, setLanguage, isRTL } = useTranslation();
  const logout = useAuthStore((s) => s.logout);
  
  const [notifications, setNotifications] = useState({
    newLead: true,
    stageChange: true,
    messages: false,
    dailyReport: true,
  });

  const handleSave = () => {
    toast.success(t('settings.saved'));
  };

  const handleLogout = () => {
    logout();
    toast.info(t('auth.loggedOut'));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">{t('settings.title')}</h1>
        <p className="text-slate-400 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* User Info */}
      <section className="card">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <User className="w-5 h-5" />
          {t('settings.userInfo')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">{t('settings.displayName')}</label>
            <input type="text" className="input w-full" defaultValue="Ahmed" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">{t('settings.email')}</label>
            <input type="email" className="input w-full" defaultValue="ahmed@company.sa" />
          </div>
        </div>
      </section>

      {/* Appearance */}
      <section className="card">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Moon className="w-5 h-5" />
          {t('settings.appearance')}
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white">{t('settings.darkMode')}</p>
              <p className="text-sm text-slate-400">{t('settings.darkModeDesc')}</p>
            </div>
            <button className="px-4 py-2 rounded-lg bg-primary-500/20 text-primary-400 flex items-center gap-2">
              <Moon className="w-4 h-4" />
              {t('common.active')}
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white">{t('settings.language')}</p>
              <p className="text-sm text-slate-400">{t('settings.languageDesc')}</p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setLanguage('ar')}
                className={cn('px-4 py-2 rounded-lg flex items-center gap-2', language === 'ar' ? 'bg-primary-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700')}
              >
                العربية
              </button>
              <button 
                onClick={() => setLanguage('en')}
                className={cn('px-4 py-2 rounded-lg flex items-center gap-2', language === 'en' ? 'bg-primary-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700')}
              >
                English
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="card">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5" />
          {t('settings.notifications')}
        </h2>
        
        <div className="space-y-3">
          {[
            { key: 'newLead', label: t('settings.notifyNewLead'), desc: t('settings.notifyNewLeadDesc') },
            { key: 'stageChange', label: t('settings.notifyStageChange'), desc: t('settings.notifyStageChangeDesc') },
            { key: 'messages', label: t('settings.notifyMessages'), desc: t('settings.notifyMessagesDesc') },
            { key: 'dailyReport', label: t('settings.notifyDailyReport'), desc: t('settings.notifyDailyReportDesc') },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between py-2">
              <div>
                <p className="text-white">{item.label}</p>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifications(n => ({ ...n, [item.key]: !n[item.key as keyof typeof n] }))}
                className={cn('w-12 h-6 rounded-full transition-colors relative', notifications[item.key as keyof typeof notifications] ? 'bg-primary-500' : 'bg-slate-700')}
              >
                <span className={cn('absolute top-1 w-4 h-4 rounded-full bg-white transition-all', notifications[item.key as keyof typeof notifications] ? 'right-1' : 'left-1')} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex justify-between">
        <button onClick={handleLogout} className="btn-secondary text-red-400 hover:bg-red-500/10">
          <LogOut className="w-4 h-4" />
          {t('settings.logout')}
        </button>
        <button onClick={handleSave} className="btn-primary">
          <Save className="w-4 h-4" />
          {t('settings.save')}
        </button>
      </div>
    </div>
  );
}
